import { useAuth } from "../context/AuthContext";
import { startSubscriptionCheckout } from "../lib/publicCheckout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Logo, BRAND_NAME, BRAND_SUBTITLE } from "../components/ui/Logo";
import { Calendar, LogOut } from "lucide-react";
import { isUsClinicalHost, USA_PRO_MONTHLY_PRICE_LABEL } from "../lib/usClinicalHost";

export default function BillingRequired() {
  const isUsHost = isUsClinicalHost();
  const { user, logout, isBillingExempt } = useAuth();
  const reason = user?.access_blocked_reason || "trial_expired";
  const isTrialExpired = reason === "trial_expired";
  const isPastDue = reason === "past_due";
  const isCanceled = reason === "canceled";

  const title = isUsHost
    ? isTrialExpired
      ? "Your trial has ended"
      : isPastDue
        ? "Payment past due"
        : isCanceled
          ? "Subscription canceled"
          : "Access paused"
    : isTrialExpired
      ? "Tu período de prueba ha finalizado"
      : isPastDue
        ? "Pago pendiente"
        : isCanceled
          ? "Suscripción cancelada"
          : "Acceso suspendido";

  const description = isUsHost
    ? isTrialExpired
      ? "Your 14-day free trial has ended. Subscribe to keep using Aurora Geriatric without interruption."
      : isPastDue
        ? "We could not process your last payment. Update your payment method to restore access."
        : isCanceled
          ? "Your subscription was canceled. Reactivate to continue using Aurora Geriatric."
          : "Update your subscription to continue using Aurora Geriatric."
    : isTrialExpired
      ? "Tu prueba gratuita de 14 días ha finalizado. Para seguir usando Aurora Geriátrica, activa tu suscripción."
      : isPastDue
        ? "Hay un problema con tu pago. Actualiza tu método de pago para continuar."
        : isCanceled
          ? "Tu suscripción ha sido cancelada. Reactívala para acceder a Aurora Geriátrica."
          : "Actualiza tu suscripción para continuar usando Aurora Geriátrica.";

  const planTitle = isUsHost ? "Aurora Geriatric Professional" : "Aurora Geriátrica Profesional";
  const cardDescription = isUsHost
    ? `${USA_PRO_MONTHLY_PRICE_LABEL} · Unlimited patients and visits · 14-day free trial`
    : "$39 USD / mes · Pacientes y consultas ilimitados · 14 días gratis";

  const bullets = isUsHost
    ? [
        "Comprehensive geriatric assessment workflows",
        "Interactive clinical scales",
        "Clinical documentation and visit summaries",
        "Longitudinal history by patient",
      ]
    : [
        "Consulta geriátrica estructurada",
        "Resultados históricos",
        "Documentos clínicos y recetas",
        "Historial longitudinal por paciente",
      ];

  const ctaLabel = isUsHost ? "Subscribe now" : "Activar suscripción";
  const redirectNote = isUsHost
    ? "You will be redirected to Lemon Squeezy to complete payment securely."
    : "Serás redirigido a Lemon Squeezy para completar el pago de forma segura.";
  const signOutLabel = isUsHost ? "Sign out" : "Cerrar sesión";

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-sky-50 flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-8">
          <Logo variant="wordmark" format="png" size="lg" className="mb-4" />
          <h2 className="text-xl font-bold text-slate-800 tracking-tight text-center">{BRAND_NAME}</h2>
          <p className="text-sm text-slate-500 text-center">{BRAND_SUBTITLE}</p>
          <h1 className="text-2xl font-bold text-slate-800 tracking-tight text-center mt-6">{title}</h1>
          <p className="text-slate-600 text-center mt-2">{description}</p>
        </div>

        <Card className="border-slate-200 shadow-xl">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-sky-600" />
              {planTitle}
            </CardTitle>
            <CardDescription>{cardDescription}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <ul className="text-sm text-slate-600 space-y-2">
              {bullets.map((line) => (
                <li key={line}>• {line}</li>
              ))}
            </ul>
            <Button
              className="w-full bg-slate-800 hover:bg-slate-700"
              disabled={isBillingExempt}
              onClick={() => {
                if (isBillingExempt) return;
                startSubscriptionCheckout({
                  userId: user?.id,
                  email: user?.email,
                  name: user?.nombre,
                });
              }}
            >
              {ctaLabel}
            </Button>
            <p className="text-xs text-slate-500 text-center">{redirectNote}</p>
          </CardContent>
        </Card>

        <div className="mt-6 flex justify-center">
          <Button variant="ghost" onClick={logout} className="text-slate-600">
            <LogOut className="h-4 w-4 mr-2" />
            {signOutLabel}
          </Button>
        </div>

        <footer className="mt-8 text-center text-slate-400 text-xs">
          <p className="font-medium text-sky-600">{BRAND_NAME}</p>
          <p>{BRAND_SUBTITLE}</p>
        </footer>
      </div>
    </div>
  );
}
